// Constants of the Chat Agent v2 integration (vAI agent API /ai/v2).
// Contract: docs/explanation/chat-agent-v2.md.

export const AGENT_RUN_EVENT_TYPES = {
  RUN_STARTED: 'run.started',
  MESSAGE_DELTA: 'message.delta',
  RUN_ACTIVITY: 'run.activity',
  ASSET_READY: 'asset.ready',
  SOURCES_READY: 'sources.ready',
  SUGGESTIONS_READY: 'suggestions.ready',
  CONVERSATION_TITLED: 'conversation.titled',
  RUN_COMPLETED: 'run.completed',
  RUN_FAILED: 'run.failed',
  RUN_CANCELLED: 'run.cancelled',
} as const

export const AGENT_TERMINAL_EVENT_TYPES: readonly string[] = [
  AGENT_RUN_EVENT_TYPES.RUN_COMPLETED,
  AGENT_RUN_EVENT_TYPES.RUN_FAILED,
  AGENT_RUN_EVENT_TYPES.RUN_CANCELLED,
]

export const AGENT_RUN_STATUSES = {
  QUEUED: 'queued',
  RUNNING: 'running',
  CANCEL_REQUESTED: 'cancel_requested',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export const AGENT_TERMINAL_RUN_STATUSES: readonly string[] = [
  AGENT_RUN_STATUSES.COMPLETED,
  AGENT_RUN_STATUSES.FAILED,
  AGENT_RUN_STATUSES.CANCELLED,
]

export const AGENT_MESSAGE_STATUSES = {
  COMPLETE: 'complete',
  STREAMING: 'streaming',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export const AGENT_MESSAGE_ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant',
} as const

export const AGENT_PART_TYPES = {
  TEXT: 'text',
  IMAGE_ASSET: 'image_asset',
  FILE_ASSET: 'file_asset',
  CITATION: 'citation',
} as const

export const AGENT_ASSET_STATES = {
  UPLOADED: 'uploaded',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
} as const

export const AGENT_ASSET_ORIGINS = {
  UPLOAD: 'upload',
  GENERATED: 'generated',
  EDITED: 'edited',
} as const

// Asset `kind` of an image; every other kind is sent as a file_asset part.
export const AGENT_ASSET_KIND_IMAGE = 'image'

export const AGENT_ACTIVITY_PHASES = {
  TOOL_STARTED: 'tool_started',
  TOOL_FINISHED: 'tool_finished',
  SKILL_ACTIVATED: 'skill_activated',
} as const

export const AGENT_ERROR_CODES = {
  CONVERSATION_NOT_FOUND: 'conversation_not_found',
  RUN_NOT_FOUND: 'run_not_found',
  ASSET_NOT_FOUND: 'asset_not_found',
  RUN_ACTIVE: 'run_active',
  IDEMPOTENCY_CONFLICT: 'idempotency_conflict',
  ASSET_ATTACHED: 'asset_attached',
  REPLAY_EXPIRED: 'replay_expired',
  ASSET_TOO_LARGE: 'asset_too_large',
  UNSUPPORTED_TYPE: 'unsupported_type',
  ASSET_NOT_READY: 'asset_not_ready',
  ASSET_ALREADY_SENT: 'asset_already_sent',
  DEFINITION_UNAVAILABLE: 'definition_unavailable',
  DISPATCH_FAILED: 'dispatch_failed',
  ADMIN_REQUIRED: 'admin_required',
  SKILL_NOT_FOUND: 'skill_not_found',
  SKILL_EXISTS: 'skill_exists',
  SKILL_LIMIT: 'skill_limit',
  INVALID_SKILL: 'invalid_skill',
  INVALID_INSTRUCTIONS: 'invalid_instructions',
  INSTRUCTIONS_CONFLICT: 'instructions_conflict',
} as const

export const HTTP_STATUS_UNAUTHORIZED = 401
export const HTTP_STATUS_GONE = 410

// Enforced by getFileRejection once a file is picked — DEV-1383 dropped the
// matching `accept` on the composer's file input, so the OS picker no longer
// filters or greys out any file before that check runs.
export const AGENT_UPLOAD_EXTENSIONS: readonly string[] = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'docx', 'pdf', 'xlsx']
// Backend AGENT_UPLOAD_MAX_BYTES default (25 MiB).
export const AGENT_UPLOAD_MAX_BYTES = 25 * 1024 * 1024
export const AGENT_UPLOAD_FORM_FIELD = 'files'

export const AGENT_IDEMPOTENCY_HEADER = 'Idempotency-Key'
export const AGENT_LAST_EVENT_ID_HEADER = 'Last-Event-ID'
export const AGENT_EVENT_STREAM_MIME = 'text/event-stream'

// Backend maximum for GET /conversations?limit=.
export const AGENT_CONVERSATIONS_PAGE_SIZE = 100
export const AGENT_ASSET_POLL_INTERVAL_MS = 1500
export const AGENT_RUN_POLL_INTERVAL_MS = 2000
export const AGENT_STREAM_MAX_RECONNECTS = 3
export const AGENT_STREAM_RECONNECT_DELAY_MS = 1000
// Distance from the bottom (px) under which the message list keeps following new text.
export const AGENT_AUTOSCROLL_THRESHOLD_PX = 120
// How long the copy button shows its "copied" state (matches v1's VAIMessage).
export const AGENT_COPY_FEEDBACK_DURATION_MS = 3000
export const AGENT_COPY_ACTION_LABEL = 'Copy'
export const AGENT_COPY_DONE_LABEL = 'Copied!'

export const AGENT_LOCAL_ID_PREFIX = 'local-'
export const AGENT_UNTITLED_CONVERSATION = 'New conversation'

// Status line shown while the agent runs a tool, keyed by backend tool name
// (definitions/chat_agent/agent.yaml in ai-parser).
export const AGENT_ACTIVITY_LABELS: Readonly<Record<string, string>> = {
  list_conversation_assets: 'Reading the file…',
  read_document_blocks: 'Reading the file…',
  read_sheet_range: 'Reading the file…',
  generate_image: 'Generating an image…',
  edit_image: 'Editing the image…',
  create_docx: 'Creating a document…',
  create_xlsx: 'Creating a spreadsheet…',
  list_resources: 'Searching company resources…',
  describe_resource: 'Reading a resource…',
  search_resources: 'Searching company resources…',
  read_resource: 'Reading a resource…',
  filter_table: 'Reading a resource…',
  research_resources: 'Researching company resources…',
  list_evidence: 'Checking sources…',
  cite: 'Checking sources…',
  web_search: 'Searching the web…',
  submit_dossier: 'Summarizing findings…',
}
export const AGENT_ACTIVITY_FALLBACK_LABEL = 'Working…'

// Past-tense counterpart of AGENT_ACTIVITY_LABELS, shown once a step is done.
export const AGENT_ACTIVITY_DONE_LABELS: Readonly<Record<string, string>> = {
  list_conversation_assets: 'Read the file',
  read_document_blocks: 'Read the file',
  read_sheet_range: 'Read the file',
  generate_image: 'Generated an image',
  edit_image: 'Edited the image',
  create_docx: 'Created a document',
  create_xlsx: 'Created a spreadsheet',
  list_resources: 'Searched company resources',
  describe_resource: 'Read a resource',
  search_resources: 'Searched company resources',
  read_resource: 'Read a resource',
  filter_table: 'Read a resource',
  research_resources: 'Researched company resources',
  list_evidence: 'Checked sources',
  cite: 'Checked sources',
  web_search: 'Searched the web',
  submit_dossier: 'Summarized findings',
}
export const AGENT_ACTIVITY_DONE_FALLBACK_LABEL = 'Done'

// Internal bookkeeping tools that must never show as a step: they still
// count as activity for the "Working…" fallback while nothing else runs.
export const AGENT_ACTIVITY_HIDDEN_TOOLS: readonly string[] = ['activate_skill', 'suggest_replies']

export const AGENT_ACTIVITY_STEP_STATUSES = {
  RUNNING: 'running',
  DONE: 'done',
} as const
// Toggle under a finished answer that expands its recorded steps.
export const AGENT_ACTIVITY_STEPS_SUMMARY_LABEL = 'Steps'

export const AGENT_DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.'
export const AGENT_CONVERSATIONS_LOAD_ERROR = 'Could not load conversations.'
export const AGENT_CONVERSATION_LOAD_ERROR = 'Could not load this conversation.'
export const AGENT_UNSUPPORTED_FILE_MESSAGE = 'this file type is not supported. Use PNG, JPG, GIF, WEBP, HEIC, DOCX, PDF or XLSX.'
export const AGENT_FILE_TOO_LARGE_MESSAGE = 'files are limited to 25 MB.'
export const AGENT_ASSET_FAILED_MESSAGE = 'the file could not be processed.'
export const AGENT_ASSET_MISSING_MESSAGE = 'the file is no longer available.'
export const AGENT_DOWNLOAD_ERROR_MESSAGE = 'Could not download the file.'
export const AGENT_POPUP_BLOCKED_MESSAGE = 'Allow pop-ups for this site to open the file.'
export const AGENT_IMAGE_OPEN_ERROR_MESSAGE = 'Could not open the image.'
export const AGENT_PREVIEW_ERROR_MESSAGE = 'Could not open the preview.'
export const AGENT_DOWNLOADING_LABEL = 'Downloading file...'
// Shown and saved under this name when an asset part carries no name.
export const AGENT_ASSET_FALLBACK_NAME = 'file'

// Actions of the "⋯" menu on a file in a message (no Delete: a sent or
// produced asset cannot be deleted, the backend answers 409 asset_attached).
export const AGENT_ASSET_ACTIONS = {
  PREVIEW: 'preview',
  DOWNLOAD: 'download',
} as const
export const AGENT_ASSET_ACTION_LABELS = {
  [AGENT_ASSET_ACTIONS.PREVIEW]: 'Preview',
  [AGENT_ASSET_ACTIONS.DOWNLOAD]: 'Download',
} as const
// Above the chat panel, as the v1 attachment menu.
export const AGENT_ASSET_ACTIONS_Z_INDEX = 1010

// MIME type of a previewable file whose part carries none, by file extension.
export const AGENT_ASSET_MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

// Messages we word ourselves; every other code shows the backend's own message.
export const AGENT_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  [AGENT_ERROR_CODES.RUN_ACTIVE]: 'vAI is still answering the previous message.',
  [AGENT_ERROR_CODES.IDEMPOTENCY_CONFLICT]: 'This message was already sent.',
  [AGENT_ERROR_CODES.CONVERSATION_NOT_FOUND]: 'This conversation no longer exists.',
  [AGENT_ERROR_CODES.DEFINITION_UNAVAILABLE]: 'vAI is temporarily unavailable. Please try again in a moment.',
  [AGENT_ERROR_CODES.DISPATCH_FAILED]: 'vAI is temporarily unavailable. Please try again in a moment.',
  [AGENT_ERROR_CODES.ADMIN_REQUIRED]: 'Only company administrators can do this.',
  [AGENT_ERROR_CODES.SKILL_NOT_FOUND]: 'This skill no longer exists.',
  [AGENT_ERROR_CODES.SKILL_EXISTS]: 'A skill with this key already exists. Change the key and try again.',
  [AGENT_ERROR_CODES.INSTRUCTIONS_CONFLICT]: 'Another administrator saved the instructions at the same time. Save again to keep your text.',
}

export const AGENT_ACTIVITY_STOPPING_LABEL = 'Stopping…'
export const AGENT_MESSAGE_STOPPED_LABEL = 'Stopped'
// A failed answer whose run failure could not be read (see restoreFailure).
export const AGENT_MESSAGE_FAILED_FALLBACK = 'This answer could not be completed.'
export const AGENT_WEB_REF_PREFIX = 'web:'
export const AGENT_CITATION_KIND_LABELS = {
  WEB: 'Web',
  RESOURCE: 'Resource',
} as const

export const AGENT_SOURCES_LABEL = 'Sources'
export const AGENT_SOURCES_CLOSE_LABEL = 'Close sources'
// The panel's DOM id: the Sources button checks focus against it before
// taking focus back when the panel closes.
export const AGENT_SOURCES_PANEL_ID = 'agent-chat-sources-panel'
export const AGENT_SOURCE_MARKER_ARIA_LABEL = (label: string, extraCount: number): string => (
  extraCount > 0 ? `Source: ${label} and ${extraCount} more` : `Source: ${label}`
)
export const AGENT_SOURCE_MARKER_EXTRA_LABEL = (extraCount: number): string => `+${extraCount}`
export const AGENT_SOURCE_CARD_PREVIOUS_LABEL = 'Previous source'
export const AGENT_SOURCE_CARD_NEXT_LABEL = 'Next source'
// The card opens a beat after the pointer lands on a pill (so sweeping over
// the text does not flash cards) and closes a beat after it leaves (so the
// pointer can travel from the pill onto the card).
export const AGENT_SOURCE_CARD_OPEN_DELAY_MS = 150
export const AGENT_SOURCE_CARD_CLOSE_DELAY_MS = 200
export const AGENT_SOURCE_CARD_OFFSET_PX = 6
export const AGENT_SOURCE_CARD_VIEWPORT_PADDING_PX = 8
export const AGENT_SOURCES_BUTTON_MAX_ICONS = 3
// Web sources only — resources never request a favicon. The user's browser
// sends the source's (public) domain to Google to fetch it.
export const AGENT_SOURCE_FAVICON_DOMAIN_PLACEHOLDER = '{domain}'
export const AGENT_SOURCE_FAVICON_URL_TEMPLATE = `https://www.google.com/s2/favicons?domain=${AGENT_SOURCE_FAVICON_DOMAIN_PLACEHOLDER}&sz=32`
// Public suffixes made of two labels: `bbc.co.uk` is labelled `bbc`, not `co`.
export const AGENT_SOURCE_TWO_LEVEL_SUFFIXES: readonly string[] = [
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk',
  'com.au', 'net.au', 'org.au', 'gov.au',
  'co.jp', 'co.nz', 'co.za', 'co.in',
  'com.br', 'com.mx', 'com.tr', 'com.cn',
  'com.ua', 'gov.ua',
]
export const AGENT_NEW_TAB_TARGET = '_blank'
export const AGENT_NEW_TAB_REL = 'noopener noreferrer'
export const AGENT_RESOURCE_ROUTE_NAME = 'resource'

export const AGENT_DELETE_CONVERSATION_TITLE = 'Delete this conversation?'
export const AGENT_DELETE_CONVERSATION_MESSAGE = 'This cannot be undone.'
export const AGENT_DELETE_CONVERSATION_SUBMIT = 'Delete'

// Company skills (Chat Agent admin → Skills). Limits mirror ai-parser's
// validation (DEV-1371); the server still enforces them.
export const AGENT_SKILL_SOURCES = {
  PLATFORM: 'platform',
  COMPANY: 'company',
} as const
export const AGENT_SKILL_NAME_MAX_LENGTH = 80
export const AGENT_SKILL_DESCRIPTION_MAX_LENGTH = 200
export const AGENT_SKILL_INSTRUCTIONS_MAX_LENGTH = 8000
export const AGENT_SKILL_KEY_MIN_LENGTH = 2
export const AGENT_SKILL_KEY_MAX_LENGTH = 40
// Lowercase letters, digits and hyphens, not starting with a hyphen.
export const AGENT_SKILL_KEY_PATTERN = /^[a-z0-9][a-z0-9-]*$/
// Per company, enabled or not; the server answers 409 skill_limit above it.
export const AGENT_SKILL_MAX_PER_COMPANY = 20
// Form fields an invalid_skill error can point at.
export const AGENT_SKILL_FORM_FIELDS = {
  NAME: 'name',
  DESCRIPTION: 'description',
  INSTRUCTIONS: 'instructions',
  KEY: 'key',
} as const
export const AGENT_SKILL_LIMIT_MESSAGE = (limit: number): string => `A company can have at most ${limit} skills.`
export const AGENT_SKILLS_LOAD_ERROR = 'Could not load skills.'
// A skill_activated event of a company skill becomes a finished step in the
// chat; built-in skill ids carry no prefix and stay hidden.
export const AGENT_COMPANY_SKILL_ID_PREFIX = 'company:'
export const AGENT_SKILL_STEP_TOOL = 'skill'
export const AGENT_SKILL_STEP_LABEL = (name: string): string => `Used ${name}`
export const AGENT_SKILL_STEP_FALLBACK_LABEL = 'Used a skill'

// Company Instructions (Chat Agent admin → Instructions, ai-parser MR !526).
// The server sends its cap as max_chars; this is only the value to show
// before the first load answers.
export const AGENT_INSTRUCTIONS_DEFAULT_MAX_CHARS = 8000
export const AGENT_INSTRUCTIONS_LOAD_ERROR = 'Could not load the instructions.'
export const AGENT_INSTRUCTIONS_SAVE_ERROR = 'Failed to save the instructions'
export const AGENT_INSTRUCTIONS_TOO_LONG_MESSAGE = (maxChars: number): string => (
  `Instructions must be at most ${maxChars} characters.`
)
